import { isDefined, isNotDefined } from '@sgme/fp';
import deepEqual from 'fast-deep-equal';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { selectCurrencyPairPrecision } from '@/api/config/selectors.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { selectSwapTile } from '@/store/swap/selectors/tile.ts';
import { convertRateToPoints } from '@/utils/number-point.ts';

export type SwapDoddFranckMidPriceProps = {
  tileId: string;
};

export const SwapDoddFranckMidPrice = (props: SwapDoddFranckMidPriceProps) => {
  const { tileId } = props;

  const { currencyPairPrecision, nearLegPrice, midPoints } = useAppSelector((state) => {
    const tile = selectSwapTile(state, tileId);

    if (
      isNotDefined(tile) ||
      isNotDefined(tile.properties.currencyPair) ||
      isNotDefined(tile.properties.rfs) ||
      tile.properties.rfs.status !== 'STREAMED'
    ) {
      return {
        currencyPairPrecision: 0,
      };
    }

    const currencyPairPrecision = selectCurrencyPairPrecision(state, tile.properties.currencyPair) ?? 0;

    const midPoints = isDefined(tile.properties.rfs.quote.delta)
      ? convertRateToPoints(tile.properties.rfs.quote.delta, currencyPairPrecision - 1)
      : undefined;

    return {
      nearLegPrice: tile.properties.rfs.quote.midAllInRate,
      midPoints,
      currencyPairPrecision,
    };
  }, deepEqual);

  return (
    <div className="text-center fs-12 text-secondary" data-e2e="dodd-franck-mid-price">
      {isDefined(nearLegPrice) && isDefined(midPoints) ? (
        <>
          <FormattedMessage id="SwapTile.doddFranckMidPrice.midPrice" /> <br />
          <FormattedMessage id="SwapTile.doddFranckMidPrice.nearLeg" />:{' '}
          <span data-e2e="dodd-franck-mid-near-price">
            <FormattedNumber
              value={nearLegPrice}
              minimumFractionDigits={currencyPairPrecision + 1}
              maximumFractionDigits={currencyPairPrecision + 1}
              signDisplay="exceptZero"
            />
          </span>{' '}
          / <FormattedMessage id="SwapTile.doddFranckMidPrice.midPoints" />:{' '}
          <span data-e2e="dodd-franck-mid-far-price">
            <FormattedNumber
              value={midPoints}
              minimumFractionDigits={2}
              maximumFractionDigits={2}
              signDisplay="exceptZero"
            />
          </span>
        </>
      ) : (
        <FormattedMessage id="SwapTile.doddFranckMidPrice.reprice" values={{ br: <br /> }} />
      )}
    </div>
  );
};
